import { ComponentType, ForwardRefExoticComponent, KeyboardEventHandler, ReactNode } from "react";
import { Action, AnyAction, Reducer } from "redux";

// kvůli prevenci vůči cyklické závislosti je nutné importovat z constants a ne přes index
import { TAB_INDEX_FOCUSABLE_ONLY, TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "@multiweb/reactUtils/constants";
import { IconCode } from "@multiweb/types/contracts";
import { ExtendedPropertiesType } from "@multiweb/types/multiweb";
import { Role } from "@multiweb/uiShared/Role";
import { StrictEffect } from "@redux-saga/types";
import { ActionType, OrderType } from "../multiweb";
// kvůli prevenci vůči cyklické závislosti je nutné exportovat z constants a ne přes index
export { ICoreState } from "../../core/types";
export { IResultAction, IResultActionError, IResultActionSuccess } from "../../resultActions";
export { IAppState } from "../../setup/types";
export { TAB_INDEX_FOCUSABLE_ORDER_EXPLICIT } from "@multiweb/reactUtils/constants";

/**
 * Definice modulu aplikace. Společná pro core moduly i business moduly.
 *
 * NAME má generický typ založený na stringu, který umožní zadat jako typ konkrétní hodnotu stringu.
 * Díky tomu se dá NAME z konkrétního modulu použít jako konstanta, například při tvorbě stavu z reducerů.
 */
export interface ApplicationModule<N extends string, C = EmptyObjectType> {
    /**
     * Jméno modulu. Slouží pro identifikaci modulu a musí být unikátní.
     */
    readonly NAME: N;
    /**
     * Pokud modul vyžaduje asynchronní procesy, je pomocí ságy.
     */
    readonly saga?: SagaWithReturnType;
    /**
     * Pokud modul poskytuje uživatelské rozhraní, poskytne svoji kořenovou komponentu pomocí tohoto atributu.
     */
    readonly Container?: ComponentType<C>;
}

/**
 * Modul aplikace, který pracuje se stavem a tudíž definuje reducer.
 */
export interface ApplicationStatefulModule<N extends string, R extends Reducer = Reducer, C = EmptyObjectType>
    extends ApplicationModule<N, C> {
    /**
     * Pokud pro svou funkci modul vyžaduje stav v reduxu, definuje ho pomocí reduceru.
     */
    readonly reducer: R;
}

/**
 * Typ pro prázdný objekt.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export type EmptyObjectType = {};

/**
 * Typ pro objekt.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export type ObjectType = object;

/**
 * Typ pro prázdné props. Typicky se používá v mapStateToProps kontejnerů, které nepotřebují zvenčí dostávat žádné props.
 */
export type NoneOwnProps = EmptyObjectType;

/**
 * Typ s identifikátorem pro automatické testovací nástroje. Přidává identifikátor, který mohou testy používat. Nesmí mít žádný vliv na funkcionalitu komponenty.
 */
export type QAProps = {
    /** Textový identifikátor pro automatické testy */
    qa?: string;
};

/**
 * Typescript utilita vracející union type s názvy props
 * @example
 * type T1 = { a: string; b:string};
 * type T2 = {b:string};
 * KnownKeys<T1> //returns "a" | "b"
 * KnownKeys<T1> //returns "b"
 * @example Příklad pro rozdíl dvou typů
 * Omit<T1, KnownKeys<T2> //returns {a: string}
 */
export type KnownKeys<T> = keyof {
    [K in keyof T as string extends K ? never : number extends K ? never : K]: never
};

/**
 * Typ, který umožňuje nastavit posluchače pro události vyvolané stisknutím kláves.
 */
export type KeyboardNavigationProps = Readonly<{
    /** Pro událost keyDown. */
    onKeyDown?: KeyboardEventHandler;
    /** Pro událost keyUp. */
    onKeyUp?: KeyboardEventHandler;
    /** Pro událost keyPress. */
    onKeyPress?: KeyboardEventHandler;
}>;

/**
 * Typ přidávající vlastnosti, které slouží k nastavení přístupnosti komponenty.
 */
export type AccessibilityProps = Readonly<{
    /** Role elementu. */
    role?: Role;
    /** Umožňuje nastavit tabindex - výchozí hodnota je undefined, chování tedy závisí na prohlížeči. */
    tabIndex?: OptionalTabIndex;
    /** Příznak, zda se má skrýt zvýraznění okolo elementu při jeho zaměření (nastavuje vlastnost outline:none). */
    hideOutlineOnFocus?: boolean;
    /** Druh řazení. */
    ariaSort?: OrderType;
    /** Příznak, zdali je element zaškrtnut či nikoli. Využívá se například u komponenty CheckBoxIcon. */
    ariaChecked?: boolean;
    /** Příznak, zdali je element nedostupný. Využívá se například u komponenty CheckBoxIcon. */
    ariaDisabled?: boolean;
    /** Id elementu, který popisuje hlavní podstatu objektu/komponenty. (Např. název modálu, dialogu). */
    ariaLabelledBy?: string;
    /** Id elementu, který poskytuje podrobnější informace ke komponentě. (Např. popis validačních chyb, instrukce k inputu). */
    ariaDescribedBy?: string;
    /** Příznak, jestli jde označovat více záznamů. */
    ariaMultiselectable?: boolean;
    /** Příznak, jestli je komponenta needitovatelná (jenom pro zobrazování informací). */
    ariaReadOnly?: boolean;
    /** Text popisující interaktivní element. */
    ariaLabel?: string;
    /** Text popisující daný element. */
    ariaDescription?: string;
    /** Příznak, jestli je daný element označený. */
    ariaSelected?: boolean;
}>;

/**
 * Typ komponenty při použití forwardRef.
 * Komponentu s forwardRef je potřeba explicitně natypovat, aby bylo v DevTools dostupné její jméno (místo "Anonymous").
 *
 * @template T Typ reference na react komponentu (např. HTMLButtonElement aj.).
 * @template P Typ definující props react komponenty.
 */
export type ForwardRefType<T, P> = ForwardRefExoticComponent<React.PropsWithoutRef<P> & React.RefAttributes<T>>;

/**
 * Výčet s kódy tlačítek myši (odpovídá MouseEvent.button).
 */
export enum MouseButton {
    leftButton,
    middleButton,
    rightButton,
}

/**
 * Výčet s kódy tlačítek myši (odpovídá MouseEvent.buttons).
 */
export enum MouseButtons {
    noButton = 0,
    leftButton = 1,
    rightButton = 2,
    middleButton = 4,
}

/**
 * Základ typu pro MutableTypedObject a TypedObject. Nemůže být přiřazeno přímo do exportovaného MutableTypedObject a ten pak využít
 * v TypedObject kvůli problémům s generovanými typy, viz task 27385.
 */
type TypedObjectBase<T> = { [key: string]: T };

/**
 * Typ reprezentující prázdné properties. Slouží jako náhrada za "{}". Protože ESLint pravidlo
 * typescript-eslint/ban-types zakazuje použití "{}", měl by se používat tento typ.
 */
export type EmptyProps = Record<string, any>;

/**
 * Generický typ pro objekt.
 */
export type TypedObject<T> = Readonly<TypedObjectBase<T>>;

/**
 * Generický typ pro objekt, který lze upravovat.
 */
export type MutableTypedObject<T> = TypedObjectBase<T>;

/**
 * Typ pro uzly obsahující děti a zda se objevuje i v jiných uzlech.
 */
export type RootNodeTypedObject = { [key: string]: RootNodeSubType };

/**
 * Pomocný typ pro RootNodes.
 */
export type RootNodeSubType = { children: string[]; inRootNodes: string[] };

/**
 * Obsahuje údaje o události kliknutí myši. Nepoužívá se původní událost, protože obsahuje funkce a není tedy serializovatelná
 * pro použití v akcích.
 */
export type SerializableMouseEvent = Readonly<{
    altKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
    button: MouseButton;
}>;

/**
 * Obsahuje údaje o události stisknutí klávesy. Nepoužívá se původní událost, protože obsahuje funkce a není tedy serializovatelná
 * pro použití v akcích.
 */
export type SerializableKeyEvent = Readonly<{
    altKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
    key: string;
    keyCode: number;
}>;

/**
 * Typ pro atribut.
 */
export interface IDataAttribute<T = any> {
    /** Hodnota atributu. */
    value: T;
}

/**
 * Typ pro uložená data z aplikačního serveru
 */
export type RecordType = TypedObject<IDataAttribute>;

export type EncryptedRecordType = RecordType & { encrypted?: undefined | IDataAttribute<boolean>};

/**
 * {@link RecordType} umožňující změny.
 */
export type MutableRecordType = MutableTypedObject<IDataAttribute>;

/**
 * Výčtový typ, který určuje stav načítání dat.
 */
export enum FetchingState {
    initialized = "initialized",
    request = "request",
    success = "success",
    error = "error",
}

/**
 * Typ identifikátoru ReactAction.
 * @see ReactAction
 */
export type ReactActionId = string;

/**
 * Typ pro akci, který slouží k tomu, aby se do Redux store neukládal typ IAction z MultiWebu.
 * Po inicializaci akcí typu IAction se pro každou akci vytvoří tento nový typ s potřebnými informacemi získanými
 * z akcí typu IAction. Název ReactAction je zvolen proto, že se jedná o obdobu typu IAction a používá se v React části aplikace.
 */
export type ReactAction = {
    id: ReactActionId;
    locKey?: string;
    actionType?: ActionType;
    enabled?: boolean;
    enabledVisual?: boolean;
    order?: number;
    priority?: number;
    visible?: boolean;
    isActive?: boolean;
    actionSize?: string;
    text?: string;
    iconCode?: IconCode;
    isInNode?: boolean;
    masterNode?: string;
    extendedProperties?: ExtendedPropertiesType;
};

/**
 * Typ metody pro spuštění ReactAction.
 */
export type ExecuteReactAction = (actionId: ReactActionId, recordId: string) => void;

/**
 * Typ metody pro spuštění ReactAction na řádku.
 * Row si zabalí ExucuteReactAction, aby se nemusel dál předávat RecordId.
 */
export type ExecuteRowReactAction = (actionId: ReactActionId) => void;

/**
 * Interface pro zobrazení zprávy o tom, že pro daný frame nejsou k dispozici řádky k zobrazení.
 * Může se tak stát při inicializaci, po filtrování, po fulltextovém vyhledávání.
 * Pro každou z těchto možností by se měla zobrazovat jiná ikona a jiná zpráva.
 */
export interface IEmptyMessage {
    /** Text zprávy. */
    readonly text: string;
    /** Kód určující, která ikona se zobrazí. */
    readonly iconCode: IconCode;
}

/**
 * Pomocný typ pro nastavení povinnosti na vybrané vlastnosti objektu. Hodí se pro použití v type guard u filter funkcí.
 *
 * @example
 * Object.values(items).filter((formItem): formItem is RequiredProp<IMenuItemData, "profiles"> => !!formItem.profiles?.length);
 */
export type RequiredProp<T, K extends keyof T> = Required<Pick<T, K>> & Omit<T, K>;

/**
 * Option pro select boxy.
 * @template V Typ hodnoty, kterou option reprezentuje.
 */
export type Option<V> = {
    /** Název option zobrazený uživateli */
    readonly text: string;
    /** Hodnota, kterou option reprezentuje */
    readonly value: V;
};

/**
 * Option pro selecty, která navíc obsahuje metadata generického typu.
 * @template V Typ hodnoty, kterou option reprezentuje.
 * @template T Typ hodnoty metadat
 */
export type ExtendedOption<V, T> = {
    readonly meta?: T;
} & Option<V>;

/**
 * <p>Definuje preferované (nedeprekované) hodnoty pro html-atribut `tabIndex`.
 * <p>Tento alias využívá následující typy konstant:
 * <ul>
 * <li> {@link TAB_INDEX_FOCUSABLE_ONLY}
 * <li> {@link TAB_INDEX_FOCUSABLE_TAB_SEQUENCE}
 * </ul>
 * <p>V případě potřeby typu `undefined` je možné použít alias {@link OptionalTabIndex}.
 * <p>Použití kladných čísel se nedoporučuje. Takové elementy jsou v tab pořadí upřednostňovány a může docházet k nechtěnému přeskakování ostatních elementů.
 */
export type TabIndex = typeof TAB_INDEX_FOCUSABLE_ONLY | typeof TAB_INDEX_FOCUSABLE_TAB_SEQUENCE;

/**
 * Alias pro použití s tabindex, který může být mimo typu {@link TabIndex} také `undefined`.
 */
export type OptionalTabIndex = TabIndex | undefined;

/**
 * Výčet hodnot pro HTML atribut `dir` (směr textu elementu).
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/dir
 */
export enum TextDirectionality {
    ltr = "ltr",
    rtl = "rtl",
    auto = "auto",
}

/**
 * Výčet hodnot pro zarovnání textu.
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/text-align
 */
export enum TextAlign {
    start = "start",
    end = "end",
    left = "left",
    right = "right",
    center = "center",
    justify = "justify",
}

/**
 * Obecný typ pro použití s redux storem.
 * Záměrem je používat jej jako stav v reduxu, který bude udržovat hodnotu pole (komponenty).
 *
 * Proměnné typu `ReduxCompatibleValue` by měly být použity v kombinaci s následujícími typy:
 * - boolean
 * - number
 * - string
 * - array
 * - tuple
 * - enum
 * - pojo
 *
 * Neslouži však pro uchovávání instancí tříd a referencí na funkce.
 * Důvodem je, že jak funkce, tak instance nejsou serializovatelné.
 * To by způsobovalo problémy s Redux Devtools a zapříčinilo nefunkčnost vlastností time-travelingu a debugu.
 */
export type ReduxCompatibleValue = any;

/** Typ handleru pro událost změny */
export type OnChangeHandler<T> = (value: T) => void;

/**
 * Typ reduceru, který navíc (k `state` a `action`) přijímá jeden extra parametr.
 * Hodí se např. u kombinovaných reducerů, kdy dílčí reducer je závislý na hodnotě jiného dílčího reduceru.
 */
export type ParametricReducer<S, P, A extends Action = AnyAction> = (
    state: S | undefined,
    action: A,
    argument: P,
) => S;

/**
 * Typ reduceru, který navíc (k `state` a `action`) přijímá dva extra parametry.
 * Hodí se např. u kombinovaných reducerů, kdy dílčí reducer je závislý na hodnotě dvou jiných dílčích reducerů.
 */
export type ParametricReducerWithTwoExtraArguments<S, P1, P2, A extends Action = AnyAction> = (
    state: S | undefined,
    action: A,
    argument1: P1,
    argument2: P2,
) => S;

/**
 * Typ generika `T` zadaného typu `Promise<T>`. Nebo `N`, pokud `P` není promise.
 *
 * Příklady:
 * ```
 * type NumberPromise = Promise<number>;
 * const result1: InferredPromiseResult<NumberPromise> = ...; // number
 * const result2: InferredPromiseResult<NumberPromise, string> = ...; // number
 * const result3: InferredPromiseResult<number> = ...; // never
 * const result4: InferredPromiseResult<number, string> = ...; // string
 * ```
 *
 * @template P `Promise<T>` nebo jakýkoli jiný typ.
 * @template N Použije se jako výsledek `InferredPromiseResult<P, N>` v případě, že `P` není Promise.
 */
export type InferredPromiseResult<P, N = never> = P extends Promise<infer V> ? V : N;

/**
 * Pomocný typ pro potřeby definice `SagaYieldReturn`.
 * - Představuje v podstatě to samé, co `SagaYieldReturn`.
 * - S tím, že za `P` se nedosahuje funkce, ale její návratový typ.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
type SagaYieldReturnByType<P> = P extends Generator<StrictEffect, infer RT, any> ? RT : InferredPromiseResult<P, P>;

/**
 * Návratový typ z `yield` uvnitř ságy.
 * Příklad:
 * ```
 * import {getPartOfReduxState} from "./selectors"; // vrací string
 *
 * const getSomeNumber: number => 10;
 * const getSomeNumberPromise: Promise<number> => Promise.resolve(10);
 * const getSomeNumberSaga: SagaWithReturnType<[], number> = function* getSomeNumberSaga() { return 10; };
 *
 * const someSaga: SagaWithReturnType = function* someSaga() {
 *     const someNumber1: SagaYieldReturn<typeof getSomeNumber> = yield call(getSomeNumber);                      // something1: number
 *     const someNumber2: SagaYieldReturn<typeof getSomeNumberPromise> = yield call(getSomeNumberPromise);        // something2: number
 *     const someNumber3: SagaYieldReturn<typeof getSomeNumberSaga> = yield call(getSomeNumberSaga);              // something3: number
 *     const someSelectorResult: SagaYieldReturn<typeof getPartOfReduxState> = yield select(getPartOfReduxState); // someSelectorResult: string
 * };
 * ```
 *
 * @template P Funkce, která se - přes `call` nebo `select` - předává do "handleru ságy".
 */
export type SagaYieldReturn<P extends (...args: any[]) => any> = SagaYieldReturnByType<ReturnType<P>>;

/**
 * Označí všechny vlastnosti typu jako povinné. Prochází typ do hloubky. K nastavení pouze první úrovně vlastností jako povinné slouží
 * zabudovaný typ {@link Required}.
 *
 * @template T Typ, jehož vlastnosti budou povinné.
 * @see ConstrainedDeepRequired
 */
export type DeepRequired<T> = {
    [P in keyof T]-?: DeepRequired<T[P]>;
};

/**
 * Označí všechny vlastnosti typu jako povinné. Prochází typ do hloubky, ale pouze dokud nenarazí na omezující typ. U omezujícího typu ještě nastaví vlastnosti jako povinné,
 * ale již je neprochází do hloubky.
 *
 * Omezující typ může být sestavený i jako sjednocení typů, pokud je třeba mít více omezujících typů.
 *
 * @template T Typ, jehož vlastnosti budou povinné.
 * @template C Omezující typ, pro který se nejde dále do hloubky.
 * @see DeepRequired
 * @see Required
 */
export type ConstrainedDeepRequired<T, C> = T extends C ?
    Required<T>
    : {
        [P in keyof T]-?: ConstrainedDeepRequired<T[P], C>
    };

/**
 * Obecný typ pro ságy.
 *
 * Jeho nádstavbou v porovnání se ságou z knihovny `redux-saga` je možnost definování návratového typu ságy.
 * Typ je možné využít i pro ságy, které je nutné expandovat pomocí yield*. Aktuální typování ság to neumožňuje, viz
 * https://github.com/redux-saga/redux-saga/issues/2079
 *
 * Typ je kombinací typů Saga a SagaIterator s využitím typu Generator z lib.es2015.generator.d.ts. Není tak již třeba používat SagaIterator.
 *
 * @template Args Typování parametrů ságy. Stejné použití jako u typu {@link Saga}.
 * @template RT Typ návratové hodnoty ságy. Odpovídá generiku RT z {@link SagaIterator}. Výchozí hodnota je nastavena na void, protože většina ság nevrací hodnotu.
 */
export type SagaWithReturnType<Args extends any[] = [], RT = void> = (...args: Args) => Generator<StrictEffect, RT, any>;

/** Pole dostupných barev flagů */
export const FLAG_COLORS = [
    "gray", "silver", "brown", "black", "deeporange", "red", "amber", "orange", "lime", "yellow",
    "green", "lightgreen", "aqua", "teal", "blue", "lightblue", "deeppurple", "navy", "pink", "fuchsia", "none",
] as const;

/** Dostupné barvy flagů */
export type FlagColor = typeof FLAG_COLORS[number];

/**
 * Typ, který slučuje react komponenty typu {@link ComponentType} a {@link ForwardRefType} do jednoho.
 * Používá se např. pro předání react komponenty do connectoru, pokud dopředu neznáme konkrétní typ dané komponenty.
 *
 * @template T Typ reference na react komponentu (např. HTMLButtonElement aj.).
 * @template P Typ definující props react komponenty.
 */
export type ReactComponent<T = any, P = EmptyProps> = ComponentType<P> | ForwardRefType<T, P>;

/** Typ prop s hodnotou */
export type ValuePropType = {value: unknown};

/**
 * Typ, který slučuje react komponenty typu {@link ComponentType} a {@link ForwardRefType} do jednoho. Navíc vyžaduje aby komponenta měla prop `value: unknown`
 * Používá se např. pro předání react komponenty do connectoru, pokud dopředu neznáme konkrétní typ dané komponenty.
 * Do prop `value` connector pak nastavuje hodnotu, která má komponenta zobrazovat.
 *
 * @template T Typ reference na react komponentu (např. HTMLButtonElement aj.).
 * @template P Typ definující props react komponenty.
 */
export type ReactValueComponent<T = any, P extends ValuePropType = ValuePropType> = ComponentType<P> | ForwardRefType<T, P>;

/**
 * Typ pro jednoduchý callback.
 */
export type CallbackFn = () => void;
/**
 * Typ s informacemi potřebnými k otevření editačního okna (například pro LOVAttribute).
 */
export type OpenEditWindowParams = {
    /** Unikátní klíč pro DataSource. */
    readonly dataSourceKey: string;
    /** Unikátní klíč pro záznam. */
    readonly recordId: string;
    /** Unikátní klíč pro atribut. */
    readonly attributeId: string;
    /** Název formuláře k otevření. */
    readonly editWindow: string;
};

/**
 * Typ pro konstruktor třídy.
 *
 * @example
 * ```
 * let DynamicClass: Constructor<IReactConnectorFrame> = IReactConnectorFrame;
 * const gridConnector: IReactConnectorFrame = new DynamicClass();
 * DynamicClass = ILiteListFrameConnector;
 * const llfConnector: ILiteListFrameConnector = new DynamicClass();
 *
 *
 * type ExampleParams = [string, number];
 * type ExampleConstTyped = Constructor<OpenEditWindowParams, ExampleParams>
 * type ExampleConstAny = Constructor<OpenEditWindowParams>;
 */
export type Constructor<T, U extends any[] = any[]> = new (...args: U) => T;

/**
 * Callback volaný při odposlechnutí javascriptové `change` eventy.
 * Reactí událost `onChange` se nepodobá defaultnímu javascriptovému chování události `onChange`, ale reflektuje javascriptovou událost `onInput`.
 *
 * Mezi těmito událostmi {@link https://stackoverflow.com/questions/38256332/in-react-whats-the-difference-between-onchange-and-oninput|je rozdíl}.
 */
export type OnJavaScriptChange = (e: Event) => void;

/**
 * Slouží k určení, jakým způsobem se má odkaz otevřít
 */
export enum LinkTargetOptions {
    /** V novém okně */
    blank = "_blank",
    /** Ve stávajícím okně */
    self = "_self",
    /** V nadřazaném rámci */
    parent = "_parent",
    /** V celém těle aktuálního okna */
    top = "_top",
}

/** Informace o rozměrech komponenty */
export interface IComponentSize {
    /** Šířka */
    width: number;
    /** Výška */
    height: number;
}
/**
 * Společný typ pro DataGrid, VirtualizedList.
 */
export type RowRenderer<T, X = ReactNode> = {
    (item: T, index?: number, items?: readonly T[]): X;
};

/**
 * Pomocný typ pro tvorbu klíčů.
 */
export type KeyGeneratorType<T> = RowRenderer<T, string>;
